// ShareLectureModal — TikTok/Reels-ready share card + pre-filled captions.
// Renders a downloadable 9:16 PNG generated from an SVG so students can post their A+ grade.
import { useEffect, useMemo, useRef, useState } from "react";
import { X, Copy, Check, Download, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CAPTIONS = {
  tiktok: (t) => `POV: you used AI to turn "${t.book}" chapter ${t.chapter || ""} into a full lecture + graded quiz and got ${t.grade || "an A"} 🎯\n\n#studytok #aitools #studyhack #college #highschool #lectura\n\nTry it free → ${t.link}`,
  reels:  (t) => `Turned my textbook chapter into a full lecture, slides, study guide + graded quiz in 90 seconds. Got ${t.grade || "an A"} 💚\n\n#studygram #aitools #studytips #lectura\n\n${t.link}`,
  x:      (t) => `Just turned "${t.book}" ch. ${t.chapter || ""} into a full narrated lecture + slides + printable guide + graded quiz. Got ${t.grade || "an A"}.\n\n${t.link}`,
  facebook: (t) => `Found a tool that turns any textbook chapter into a full study package — narrated lecture, slides, printable study guide, and a graded quiz. Free tier is 3 lectures with no credit card.\n\nI just aced "${t.book}" ${t.chapter ? `chapter ${t.chapter}` : ""} with a ${t.grade || "A"}. Sharing for parents / teachers / students who need it:\n\n${t.link}`,
  reddit: (t) => `Found a tool that turns any textbook chapter into a full study package — lecture, slides, printable guide, graded quiz. Free tier is 3 lectures, no card.\n\nGot ${t.grade || "an A"} on my first try with "${t.book}" ch. ${t.chapter || ""}.\n\n${t.link}`,
};

const NETWORKS = [
  { key: "tiktok",   name: "TikTok",   href: "https://www.tiktok.com/upload" },
  { key: "reels",    name: "Reels",    href: "https://www.instagram.com/" },
  { key: "facebook", name: "Facebook", href: null }, // handled specially — Facebook sharer needs a URL param
  { key: "x",        name: "X",        href: null },
  { key: "reddit",   name: "Reddit",   href: null }, // handled specially — reddit submit needs title + text params
];

// Subreddit quick-pick for the Reddit flow. Honest, on-topic communities where
// students / teachers actually hang out. Blank = post to Reddit-wide submit page.
const REDDIT_SUBS = [
  { value: "",              label: "Any subreddit" },
  { value: "APStudents",    label: "r/APStudents" },
  { value: "GetStudying",   label: "r/GetStudying" },
  { value: "college",       label: "r/college" },
  { value: "Teachers",      label: "r/Teachers" },
  { value: "Professors",    label: "r/Professors" },
];

// Split the pre-filled caption into a snappy Reddit title (first sentence, ≤ 300)
// and a body that keeps the rest for context. Reddit rejects titles > 300 chars.
const splitCaption = (caption) => {
  const firstBreak = caption.indexOf("\n");
  const rawTitle = (firstBreak > -1 ? caption.slice(0, firstBreak) : caption).trim();
  const title = rawTitle.length > 300 ? rawTitle.slice(0, 297) + "…" : rawTitle;
  const body = firstBreak > -1 ? caption.slice(firstBreak + 1).trim() : "";
  return { title, body };
};

// 9:16 SVG card (1080 x 1920). Rendered inline so we can serialize + rasterize to PNG.
const buildSvg = ({ title, book, chapter, grade }) => {
  const safeTitle = (title || "Chapter Study Guide").slice(0, 60);
  const safeBook = (book || "").slice(0, 42);
  const gradeLetter = grade || "A";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1920" width="1080" height="1920">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#F6F3EE"/>
        <stop offset="1" stop-color="#E6DDC9"/>
      </linearGradient>
      <linearGradient id="green" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#2D4A3E"/>
        <stop offset="1" stop-color="#1E3529"/>
      </linearGradient>
      <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#D4A82C"/>
        <stop offset="1" stop-color="#B8891F"/>
      </linearGradient>
    </defs>
    <rect width="1080" height="1920" fill="url(#bg)"/>

    <!-- brand -->
    <g transform="translate(80 90)">
      <rect x="0" y="0" width="86" height="86" rx="22" fill="url(#green)"/>
      <path d="M20 22 L20 68 L66 68 L66 32 L54 22 Z" fill="none" stroke="#F6F3EE" stroke-width="4" stroke-linejoin="round"/>
      <line x1="30" y1="36" x2="56" y2="36" stroke="#F6F3EE" stroke-width="3"/>
      <line x1="30" y1="48" x2="56" y2="48" stroke="#F6F3EE" stroke-width="3"/>
      <line x1="30" y1="58" x2="48" y2="58" stroke="#F6F3EE" stroke-width="3"/>
      <text x="110" y="60" font-family="Georgia,serif" font-size="52" font-weight="700" fill="#1E3529">Lectura<tspan fill="#D4A82C">.</tspan></text>
    </g>

    <!-- huge grade -->
    <g transform="translate(540 640)">
      <circle r="280" fill="url(#green)"/>
      <text text-anchor="middle" y="130" font-family="Georgia,serif" font-size="520" font-weight="700" fill="#F6F3EE">${gradeLetter}</text>
    </g>

    <!-- caption -->
    <text x="540" y="1080" text-anchor="middle" font-family="Inter,sans-serif" font-size="34" font-weight="700" fill="#B8891F" letter-spacing="6">MY QUIZ RESULT</text>

    <text x="540" y="1180" text-anchor="middle" font-family="Georgia,serif" font-size="68" font-weight="700" fill="#1E3529">${escapeXml(safeTitle)}</text>
    <text x="540" y="1250" text-anchor="middle" font-family="Inter,sans-serif" font-size="36" fill="#4B4A44">${escapeXml(safeBook)}${chapter ? ` · Ch. ${escapeXml(String(chapter))}` : ""}</text>

    <!-- feature strip -->
    <g transform="translate(90 1420)">
      <rect x="0" y="0" width="900" height="170" rx="28" fill="#FFFFFF" stroke="#E6DDC9" stroke-width="2"/>
      <g transform="translate(60 45)" font-family="Inter,sans-serif" font-size="30" fill="#1E3529" font-weight="600">
        <circle cx="12" cy="12" r="10" fill="url(#gold)"/><text x="42" y="22">Full narrated lecture</text>
        <circle cx="12" cy="52" r="10" fill="url(#gold)"/><text x="42" y="62">AI slides + printable guide</text>
        <circle cx="12" cy="92" r="10" fill="url(#gold)"/><text x="42" y="102">Graded quiz with feedback</text>
      </g>
    </g>

    <!-- CTA -->
    <g transform="translate(540 1720)">
      <rect x="-360" y="-46" width="720" height="92" rx="46" fill="url(#green)"/>
      <text text-anchor="middle" y="14" font-family="Inter,sans-serif" font-size="38" font-weight="800" fill="#F6F3EE">getlectura.app · try 3 free</text>
    </g>
    <text x="540" y="1850" text-anchor="middle" font-family="Inter,sans-serif" font-size="26" fill="#4B4A44">Turn any book chapter into a full study package.</text>
  </svg>`;
};

const escapeXml = (s) => String(s).replace(/[<>&'"]/g, (c) => ({
  "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;",
}[c]));

const svgToPngBlob = async (svgString) => {
  const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, 1080, 1920);
    return await new Promise((res) => canvas.toBlob((b) => res(b), "image/png", 0.92));
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const ShareLectureModal = ({ open, onClose, lecture, shareLink, grade }) => {
  const [network, setNetwork] = useState("tiktok");
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [redditSub, setRedditSub] = useState("APStudents");
  const previewRef = useRef(null);

  const ctx = useMemo(() => ({
    title: lecture?.title || "Chapter Study Guide",
    book: lecture?.book_title || "",
    chapter: lecture?.chapters || "",
    grade: grade || "A",
    link: shareLink || "https://getlectura.app",
  }), [lecture, shareLink, grade]);

  const svgString = useMemo(() => buildSvg(ctx), [ctx]);
  const caption = CAPTIONS[network](ctx);

  useEffect(() => {
    if (!open || !previewRef.current) return;
    previewRef.current.innerHTML = svgString;
  }, [open, svgString]);

  if (!open) return null;

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      toast.success("Caption copied — paste it in your post");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy caption");
    }
  };

  const downloadCard = async () => {
    setDownloading(true);
    try {
      const blob = await svgToPngBlob(svgString);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lectura-${(ctx.book || "lecture").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Share card downloaded — upload it as your TikTok / Reels cover");
    } catch (e) {
      toast.error("Couldn't render the share card — try again");
    } finally {
      setDownloading(false);
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyCaption();
      return;
    }
    try {
      const blob = await svgToPngBlob(svgString);
      const file = new File([blob], "lectura-share.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: "Lectura" });
      } else {
        await navigator.share({ text: caption, title: "Lectura", url: shareLink });
      }
    } catch { /* user cancelled */ }
  };

  // Story-first flow: on mobile, share the PNG file via the OS sheet so users
  // can pick "Instagram Story" directly. On desktop, download the sticker + copy the caption.
  const shareToStory = async () => {
    try {
      const blob = await svgToPngBlob(svgString);
      const file = new File([blob], "lectura-story.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption });
        return;
      }
      // Desktop fallback — download the 1080x1920 sticker + copy caption.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lectura-story-${(ctx.book || "lecture").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      try { await navigator.clipboard.writeText(caption); } catch { /* non-blocking */ }
      toast.success("Story sticker downloaded — AirDrop it to your phone, then upload as an Instagram Story.");
    } catch (e) {
      toast.error("Couldn't build the Story sticker — try again.");
    }
  };

  const openNetwork = () => {
    const net = NETWORKS.find((n) => n.key === network);
    // Facebook has a real prefill URL. X pops the compose intent inline.
    if (network === "facebook") {
      copyCaption();
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink || "https://getlectura.app")}&quote=${encodeURIComponent(caption)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (network === "x") {
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (network === "reddit") {
      // Reddit submit endpoint accepts pre-filled title + body. When a subreddit
      // is picked, hit /r/{sub}/submit so the post lands in the right place.
      const { title, body } = splitCaption(caption);
      const base = redditSub
        ? `https://www.reddit.com/r/${encodeURIComponent(redditSub)}/submit`
        : "https://www.reddit.com/submit";
      const url = `${base}?title=${encodeURIComponent(title)}&text=${encodeURIComponent(body)}&type=TEXT`;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (!net?.href) {
      copyCaption();
      return;
    }
    copyCaption();
    window.open(net.href, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid="share-lecture-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl bg-background shadow-2xl border border-border"
      >
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" /> Share your win
            </div>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl font-bold">Post it to TikTok, Reels, or X.</h2>
            <p className="mt-1 text-sm text-muted-foreground max-w-lg">
              Download the ready-to-post share card and copy the caption. Real posts drive real signups — and you get 30 days of Pro for every friend who joins.
            </p>
          </div>
          <button
            data-testid="share-lecture-modal-close"
            onClick={onClose}
            className="rounded-full p-2 hover:bg-secondary/60 transition"
            aria-label="Close share modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-6 p-6 md:grid-cols-[minmax(0,260px)_1fr] items-start">
          {/* 9:16 preview */}
          <div className="mx-auto md:mx-0 w-[220px] sm:w-[240px] md:w-full max-w-[260px]">
            <div
              ref={previewRef}
              data-testid="share-card-preview"
              className="w-full aspect-[9/16] rounded-2xl overflow-hidden border border-border shadow-lg bg-secondary/40"
              aria-label="Downloadable Lectura share card preview"
            />
            <button
              data-testid="share-download-btn"
              onClick={downloadCard}
              disabled={downloading}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-bold hover:opacity-90 transition disabled:opacity-60"
            >
              <Download className="h-4 w-4" />
              {downloading ? "Rendering…" : "Download share card"}
            </button>
            <button
              data-testid="share-to-story-btn"
              onClick={shareToStory}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2.5 text-sm font-bold hover:opacity-90 transition"
              title="Post directly to Instagram / TikTok / Snap Story"
            >
              <Sparkles className="h-4 w-4" /> Post to Story
            </button>
          </div>

          {/* Captions + share buttons */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Pick a platform</label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {NETWORKS.map((n) => (
                <button
                  key={n.key}
                  data-testid={`share-network-${n.key}`}
                  onClick={() => setNetwork(n.key)}
                  className={`rounded-xl border-2 px-3 py-2 text-sm font-semibold transition ${
                    network === n.key ? "border-accent bg-accent/10 text-accent" : "border-border hover:bg-secondary/60"
                  }`}
                >
                  {n.name}
                </button>
              ))}
            </div>

            <label className="mt-5 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Caption</label>
            <textarea
              data-testid="share-caption"
              value={caption}
              readOnly
              rows={7}
              className="mt-2 w-full rounded-xl border border-border bg-secondary/30 p-3 text-sm font-mono leading-relaxed resize-none"
            />

            {network === "reddit" && (
              <div className="mt-3" data-testid="share-reddit-subreddit-picker">
                <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Post to which subreddit?</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {REDDIT_SUBS.map((s) => (
                    <button
                      key={s.value}
                      data-testid={`share-reddit-sub-${s.value || "any"}`}
                      onClick={() => setRedditSub(s.value)}
                      className={`rounded-full border-2 px-3 py-1 text-xs font-semibold transition ${
                        redditSub === s.value ? "border-accent bg-accent/10 text-accent" : "border-border hover:bg-secondary/60"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Tip: rule 1 on every study sub is "no low-effort promo". Add one honest sentence about your own experience before opening Reddit so it reads as a real post, not a launch ad.
                </p>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                data-testid="share-copy-caption"
                onClick={copyCaption}
                className="inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-2 text-sm font-semibold hover:bg-secondary/60"
              >
                {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy caption</>}
              </button>
              <button
                data-testid="share-open-network"
                onClick={openNetwork}
                className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-bold hover:opacity-90"
              >
                <Share2 className="h-4 w-4" /> Open {NETWORKS.find((n) => n.key === network)?.name}
              </button>
              {typeof navigator !== "undefined" && navigator.share && (
                <button
                  data-testid="share-native"
                  onClick={nativeShare}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-2 text-sm font-semibold hover:bg-secondary/60"
                >
                  <Share2 className="h-4 w-4" /> System share
                </button>
              )}
            </div>

            <p className="mt-4 text-xs text-muted-foreground">
              Tip: on TikTok/Reels, upload the card as your cover image, film a quick clip of you clicking Play on your Lectura lecture, and paste the caption. That's a 30-second post.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareLectureModal;
